// src/components/padrekko/qc/ReopenModal.jsx
//
// Phase B reopen modal. Triggered from the "Reopen" button on CaseBanner,
// which is visible only when the case is in 'complete' AND the caller's
// effective org has is_service_provider=true.
//
// Differs from SendBackModal in two ways:
//   - Target stage is a 3-way radio choice (any prior stage), not derived
//     from the previousStageFor() map. Default is operator_review.
//   - No disputed-finding flag list. Reopen is reason-only; the receiver
//     can flag findings via the normal Send Back flow at the new stage.
//
// Authorization is enforced upstream (button visibility) and by the
// backend handler (service-provider check). This modal trusts that
// the user reaching it is allowed to reopen.

import { useState } from "react";
import { Loader2, X as XIcon, AlertCircle, Unlock } from "lucide-react";
import { useQcStore, REOPEN_TARGETS } from "../../../stores/useQcStore";

const STAGE_LABELS = {
  dh_review:       "DH Review",
  customer_review: "Customer Review",
  operator_review: "Operator Review",
};
const STAGE_DESCRIPTIONS = {
  operator_review: "Operator addresses the issue; case then forward-progresses through closeout again.",
  customer_review: "Customer revisits the case; passes through Operator before closeout.",
  dh_review:       "Start over from DH; the case re-runs the full pipeline.",
};

const DEFAULT_TARGET = "operator_review";

export default function ReopenModal({ isOpen, onClose }) {
  const caseInfo       = useQcStore((s) => s.caseInfo);
  const reopen         = useQcStore((s) => s.reopen);
  const reopenInflight = useQcStore((s) => s.reopenInflight);

  const [toStage, setToStage] = useState(DEFAULT_TARGET);
  const [reason, setReason] = useState("");
  const [inlineError, setInlineError] = useState(null);

  if (!isOpen || !caseInfo) return null;
  if (caseInfo.current_stage !== "complete") {
    // Shouldn't happen — CaseBanner gates on current_stage === 'complete'.
    return null;
  }

  function handleCancel() {
    if (reopenInflight) return;
    setReason("");
    setToStage(DEFAULT_TARGET);
    setInlineError(null);
    onClose();
  }

  async function handleSubmit() {
    const trimmed = reason.trim();
    if (!trimmed) {
      setInlineError("Reason is required.");
      return;
    }
    setInlineError(null);
    const result = await reopen({ toStage, reason: trimmed });
    if (result.ok) {
      setReason("");
      setToStage(DEFAULT_TARGET);
      onClose();
    } else {
      setInlineError(result.error || "Reopen failed.");
    }
  }

  const submitEnabled = reason.trim().length > 0 && !reopenInflight;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleCancel()}
    >
      <div className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
            <Unlock size={12} className="text-emerald-400" />
            Reopen this case?
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            disabled={reopenInflight}
            className="text-fg-secondary hover:text-slate-200 transition disabled:opacity-40"
            title="Cancel"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* Target stage — required radio */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              Reopen to which stage? <span className="text-red-300">*</span>
            </label>
            <div className="rounded border border-slate-800 bg-slate-900/40">
              {REOPEN_TARGETS.map((stage) => (
                <label
                  key={stage}
                  className="flex items-start gap-2 px-2.5 py-2 cursor-pointer
                             hover:bg-slate-800/40 transition border-b
                             border-slate-800/60 last:border-b-0"
                >
                  <input
                    type="radio"
                    name="reopen_target"
                    value={stage}
                    checked={toStage === stage}
                    onChange={() => setToStage(stage)}
                    disabled={reopenInflight}
                    className="accent-emerald-400 mt-[3px]"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="text-[11px] text-slate-100 font-medium block">
                      {STAGE_LABELS[stage] ?? stage}
                    </span>
                    <span className="text-[9px] text-fg-secondary italic leading-snug block">
                      {STAGE_DESCRIPTIONS[stage] ?? ""}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Reason — required */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              Reason for reopening <span className="text-red-300">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (inlineError) setInlineError(null);
              }}
              disabled={reopenInflight}
              rows={3}
              placeholder="Explain why this closed case needs to be reopened."
              className="w-full text-[11px] px-2.5 py-2 rounded border
                         border-slate-700 bg-slate-900 text-slate-100
                         placeholder:text-slate-600
                         focus:outline-none focus:border-slate-500
                         disabled:opacity-50 leading-snug resize-none"
            />
          </div>

          {inlineError && (
            <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug">
              <AlertCircle size={10} className="mt-0.5 shrink-0" />
              <span>{inlineError}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end gap-1.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={reopenInflight}
            className="text-[11px] px-3 py-1.5 rounded border
                       border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition
                       disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!submitEnabled}
            className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded border
                       bg-emerald-500/15 border-emerald-500/50 text-emerald-200
                       hover:bg-emerald-500/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {reopenInflight && <Loader2 size={11} className="animate-spin" />}
            {reopenInflight ? "Reopening…" : "Reopen"}
          </button>
        </div>
      </div>
    </div>
  );
}
